/**
 * Custom cell renderers for the spreadsheet: favicon+text for domain/company columns,
 * validation error highlight (red from map), and highlighting for cells currently being enriched.
 */

import type { ICellCustomRender } from '@univerjs/core';
import {
  DOMAIN_COLUMN_NAME,
  LEAD_COMPANY_COLUMN_NAME
} from '../../api/spreadsheet';
import { CELL_MEASURE_FONT } from './spreadsheetConstants';
import {
  FAVICON_MIN_SIZE,
  FAVICON_SIZE,
  FIRST_COL_FAVICON_PADDING,
  getDomainHost,
  loadFavicon
} from './spreadsheetUtils';
import type {
  EnrichingCellsHighlightRenderContext,
  FaviconColumnRenderContext,
  ValidationErrorHighlightRenderContext
} from './spreadsheetTypes';

const CELL_TEXT_PADDING = 6;

/** Domain column ($Domain Name) and Company column ($Company): draw favicon + text in each cell. */
export function createFaviconColumnCustomRender(
  ctx: FaviconColumnRenderContext
): ICellCustomRender {
  return {
    zIndex: 60,
    drawWith(ctx2d, info) {
      const columnNames = ctx.getColumnNamesByIndex();
      const colName = columnNames[info.col];
      if (
        colName !== DOMAIN_COLUMN_NAME &&
        colName !== LEAD_COMPANY_COLUMN_NAME
      )
        return;
      const domainValue = info.data?.v != null ? String(info.data.v) : '';
      const hasValidationError = ctx
        .getValidationErrorsMap()
        .has(`${info.row},${info.col}`);
      const host = hasValidationError ? null : getDomainHost(domainValue);
      const displayText = domainValue || '';
      const { startX, startY, endX, endY } = info.primaryWithCoord;
      const cellW = endX - startX;
      const cellH = endY - startY;
      const padding = FIRST_COL_FAVICON_PADDING;
      let left = startX + padding;
      const midY = startY + cellH / 2;
      const styleObj = info.style as {
        bgColor?: { rgb?: string };
        bg?: { rgb?: string };
      } | null;
      const bg = styleObj?.bgColor?.rgb ?? styleObj?.bg?.rgb ?? '#ffffff';
      ctx2d.fillStyle = bg;
      const inset = 1;
      ctx2d.fillRect(
        startX + inset,
        startY + inset,
        Math.max(0, cellW - inset),
        Math.max(0, cellH - inset)
      );
      if (host) {
        const img = loadFavicon(host);
        if (
          img &&
          img.complete &&
          img.naturalWidth >= FAVICON_MIN_SIZE &&
          img.naturalHeight >= FAVICON_MIN_SIZE
        ) {
          const size = FAVICON_SIZE;
          ctx2d.drawImage(img, left, midY - size / 2, size, size);
          left += size + padding;
        }
      }
      if (displayText) {
        ctx2d.save();
        ctx2d.font = CELL_MEASURE_FONT;
        ctx2d.fillStyle = '#000';
        ctx2d.textBaseline = 'middle';
        const maxTextW = cellW - (left - startX) - padding * 2;
        let text = displayText;
        if (maxTextW > 0 && ctx2d.measureText(displayText).width > maxTextW) {
          text = '';
          for (let i = displayText.length; i >= 0; i--) {
            const t = displayText.slice(0, i) + '…';
            if (ctx2d.measureText(t).width <= maxTextW) {
              text = t;
              break;
            }
          }
        }
        if (text) ctx2d.fillText(text, left, midY);
        ctx2d.restore();
      }
    }
  };
}

/** Cells in validationErrorsMapRef: draw red background then text on top (like favicon renderer) so text is visible. */
export function createValidationErrorHighlightRender(
  ctx: ValidationErrorHighlightRenderContext
): ICellCustomRender {
  const bgRgb =
    (ctx.invalidCellStyle?.bg as { rgb?: string } | undefined)?.rgb ??
    '#ffcdd2';
  return {
    zIndex: 65,
    drawWith(ctx2d, info) {
      if (!ctx.getValidationErrorsMap().has(`${info.row},${info.col}`)) return;
      const { startX, startY, endX, endY } = info.primaryWithCoord;
      const cellW = endX - startX;
      const cellH = endY - startY;
      const inset = 1;
      // 1) Draw red background (like favicon renderer draws bg first)
      ctx2d.fillStyle = bgRgb;
      ctx2d.fillRect(
        startX + inset,
        startY + inset,
        Math.max(0, cellW - inset),
        Math.max(0, cellH - inset)
      );
      // 2) Draw cell text on top so it's visible
      let displayText = '';
      if (info.data?.v != null && info.data.v !== '') {
        displayText = String(info.data.v);
      } else if (info.data?.p != null && typeof info.data.p === 'object') {
        const p = info.data.p as { dataStream?: string };
        if (typeof p.dataStream === 'string')
          displayText = p.dataStream.replace(/\r\n?/g, ' ').trim();
      }
      if (displayText) {
        const left = startX + CELL_TEXT_PADDING;
        const midY = startY + cellH / 2;
        const maxTextW = cellW - CELL_TEXT_PADDING * 2;
        ctx2d.save();
        ctx2d.font = CELL_MEASURE_FONT;
        ctx2d.fillStyle = '#000';
        ctx2d.textBaseline = 'middle';
        let text = displayText;
        if (maxTextW > 0 && ctx2d.measureText(displayText).width > maxTextW) {
          text = '';
          for (let i = displayText.length; i >= 0; i--) {
            const t = displayText.slice(0, i) + '…';
            if (ctx2d.measureText(t).width <= maxTextW) {
              text = t;
              break;
            }
          }
        }
        if (text) ctx2d.fillText(text, left, midY);
        ctx2d.restore();
      }
    }
  };
}

/** Cells currently being executed (SSE enrichment): draw highlight with 1px inset so gridlines stay visible. */
export function createEnrichingCellsHighlightRender(
  ctx: EnrichingCellsHighlightRenderContext
): ICellCustomRender {
  return {
    zIndex: 70,
    drawWith(ctx2d, info) {
      if (!ctx.getExecutingCellKeys().has(`${info.row}:${info.col}`)) return;
      const { startX, startY, endX, endY } = info.primaryWithCoord;
      const cellW = endX - startX;
      const cellH = endY - startY;
      const inset = 1;
      ctx2d.fillStyle = ctx.enrichingCellStyle.bg.rgb;
      ctx2d.fillRect(
        startX + inset,
        startY + inset,
        Math.max(0, cellW - inset),
        Math.max(0, cellH - inset)
      );
    }
  };
}
